import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Marquee from "react-fast-marquee";
import api from "@/lib/api";
import { GeoAccents, ZigzagDivider } from "@/components/GeoAccents";

export default function DeptHome() {
  const { dept } = useOutletContext();
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/clients", { params: { department: dept.slug } }).then((r) => setClients(r.data)).catch(() => {});
    api.get("/team", { params: { department: dept.slug } }).then((r) => setTeam(r.data)).catch(() => {});
    api.get("/services", { params: { department: dept.slug } }).then((r) => setServices(r.data)).catch(() => {});
  }, [dept.slug]);

  return (
    <div>
      {/* HERO — centered USG-style */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: `url(${dept.hero_image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40" />
        <div className="absolute inset-0 bg-black/40" />

        <GeoAccents
          className="left-[6%] top-[14%] w-[42%] h-[60%] opacity-80"
          color={dept.accent}
        />

        <div className="relative z-10 text-center px-6 max-w-6xl">
          <div className="csg-label" style={{ color: dept.accent }}>
            {dept.name}
          </div>
          <h1 className="font-display uppercase leading-[0.84] text-[14vw] md:text-[8.5vw] mt-6">
            <span className="block text-white">{dept.tagline.split(" ").slice(0, -2).join(" ")}</span>
            <span className="block" style={{ color: dept.accent }}>
              {dept.tagline.split(" ").slice(-2).join(" ")}
            </span>
          </h1>
          <p className="font-body text-white/75 max-w-2xl mx-auto mt-8 text-base md:text-lg" />
          <div className="mt-10 flex flex-col sm:flex-row gap-4 items-center justify-center">
            <Link
              to="clients"
              data-testid="hero-cta-clients"
              className="csg-btn"
              style={{ backgroundColor: dept.accent }}
            >
              View Our Clients <ArrowRight size={14} />
            </Link>
            <Link to="contact" data-testid="hero-cta-contact" className="csg-btn csg-btn-ghost">
              Get In Touch <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <ZigzagDivider color={dept.accent} height={22} />

      {/* WHO WE ARE — left text, right stats with geo accents */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-14 py-24 md:py-36 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-6">
          <div className="csg-label" style={{ color: dept.accent }}>
            Who We Are
          </div>
          <h2 className="font-display uppercase leading-[0.9] mt-5 text-5xl md:text-7xl">
            <span className="block text-white">Guiding Your</span>
            <span className="block" style={{ color: dept.accent }}>
              Career Journey.
            </span>
          </h2>
          <p className="font-body text-white/70 mt-7 max-w-xl text-base md:text-lg leading-relaxed">
            Building lasting relationships with our clients, their families and the stakeholders
            within the game. From negotiation tables to brand boardrooms, we work for one thing —
            the long-term interests of our athletes.
          </p>

          <div className="grid grid-cols-2 gap-6 mt-12">
            {[
              { n: "50+", l: "Athletes Represented" },
              { n: "£5M+", l: "Contracts Negotiated" },
              { n: "10+", l: "Countries" },
              { n: "5", l: "Years Of Practice" },
            ].map((s, i) => (
              <div key={i} className="border-l-2 pl-5" style={{ borderColor: dept.accent }}>
                <div className="font-display text-5xl md:text-6xl" style={{ color: dept.accent }}>
                  {s.n}
                </div>
                <div className="csg-label text-white/60 mt-2" style={{ color: "rgba(255,255,255,0.55)" }}>
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-6 relative min-h-[460px]">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${dept.hero_image})` }}
          />
          <div className="absolute inset-0 bg-black/45" />
          <GeoAccents className="inset-0 w-full h-full" color={dept.accent} opacity={0.92} />
          <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
            <span className="csg-chip" style={{ backgroundColor: dept.accent }}>
              {dept.name}
            </span>
            <span
              className="font-display text-5xl md:text-6xl uppercase leading-[0.85] text-right"
              style={{ color: dept.accent }}
            >
              CSG
            </span>
          </div>
        </div>
      </section>

      {/* News-style featured roster cards */}
      {clients.length > 0 && (
        <>
          <section className="max-w-[1500px] mx-auto px-6 md:px-14 pb-24 md:pb-32">
            <div className="text-center mb-14">
              <div className="csg-label" style={{ color: dept.accent }}>Our Stories</div>
              <h2 className="font-display uppercase text-5xl md:text-7xl mt-4">
                Featured <span style={{ color: dept.accent }}>Clients</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              {clients.slice(0, 3).map((c) => (
                <article
                  key={c.id}
                  className="group relative overflow-hidden aspect-[3/4] bg-[#0a0a0a]"
                  data-testid={`featured-client-${c.id}`}
                >
                  <img
                    src={c.photo_url}
                    alt={c.name}
                    className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                  <span
                    className="csg-chip absolute top-5 left-5"
                    style={{ backgroundColor: dept.accent }}
                  >
                    {c.featured ? "Featured" : "Client"}
                  </span>
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-7">
                    <div className="csg-label" style={{ color: dept.accent }}>
                      {c.position} · {c.nationality}
                    </div>
                    <h3 className="font-display text-3xl md:text-4xl uppercase mt-2 leading-[0.95]">
                      {c.name}
                    </h3>
                    <p className="font-body text-sm text-white/65 mt-2">{c.team}</p>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-10 text-center">
              <Link to="clients" className="csg-btn csg-btn-ghost">
                See Full Roster <ArrowRight size={14} />
              </Link>
            </div>
          </section>
        </>
      )}

      {/* Services preview — USG-style bordered icon grid */}
      {services.length > 0 && (
        <section className="relative">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-25"
            style={{ backgroundImage: `url(${dept.hero_image})` }}
          />
          <div className="absolute inset-0 bg-black/85" />

          <div className="relative max-w-[1500px] mx-auto px-6 md:px-14 py-24 md:py-32 grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="csg-label" style={{ color: dept.accent }}>Services</div>
              <h2 className="font-display uppercase leading-[0.9] mt-5 text-5xl md:text-7xl">
                <span className="block text-white">Delivering</span>
                <span className="block" style={{ color: dept.accent }}>
                  For You.
                </span>
              </h2>
              <p className="font-body text-white/70 mt-7 max-w-md">
                Industry-leading representation across the full lifecycle of an athletic career —
                from first contract to legacy planning.
              </p>
              <Link to="services" className="csg-btn mt-7" style={{ backgroundColor: dept.accent }}>
                Explore Services <ArrowRight size={14} />
              </Link>
            </div>

            <div className="md:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-4">
              {services.slice(0, 6).map((s) => (
                <div key={s.id} className="csg-card p-6 aspect-square flex flex-col justify-between">
                  <div
                    className="w-12 h-12 grid place-items-center border"
                    style={{ borderColor: dept.accent, color: dept.accent }}
                  >
                    <span className="csg-label" style={{ color: dept.accent }}>
                      {String(services.indexOf(s) + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div>
                    <div className="csg-label text-white/40" style={{ color: dept.accent }}>
                      {s.title.split(" ")[0]}
                    </div>
                    <h3 className="font-display text-xl md:text-2xl uppercase mt-1 leading-[0.95]">
                      {s.title.split(" ").slice(1).join(" ") || s.title}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Ticker */}
      {clients.length > 0 && (
        <section className="py-7 border-y border-white/10 bg-black">
          <Marquee speed={45} gradient={false} pauseOnHover>
            {clients.map((c, i) => (
              <span
                key={c.id || i}
                className="font-display text-2xl md:text-4xl uppercase mx-10 text-white/80"
              >
                {c.name}
                <span style={{ color: dept.accent }} className="mx-3">/</span>
                <span className="text-white/45">{c.position}</span>
                <span className="text-white/15 mx-7">●</span>
              </span>
            ))}
          </Marquee>
        </section>
      )}

      {/* Team preview */}
      {team.length > 0 && (
        <section className="max-w-[1500px] mx-auto px-6 md:px-14 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="csg-label" style={{ color: dept.accent }}>Here To Help</div>
              <h2 className="font-display uppercase text-5xl md:text-7xl mt-4">
                Meet Your <span style={{ color: dept.accent }}>Team.</span>
              </h2>
            </div>
            <Link to="team" className="csg-btn csg-btn-ghost self-start md:self-end">
              Explore Team <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-5">
            {team.slice(0, 3).map((m) => (
              <article key={m.id} className="group csg-card overflow-hidden">
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={m.photo_url}
                    alt={m.name}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-display text-2xl md:text-3xl uppercase leading-[0.95]">
                    {m.name}
                  </h3>
                  <div className="csg-label mt-2" style={{ color: dept.accent }}>
                    {m.role}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
